import { isNullOrUndefined, isUndefinedOrEmpty } from '@afterpay/utils'
import { DEFAULT_INPUT_SIZE } from './constants'

const formatWithLocale = (value: string, locale: string): string => {
  if (isUndefinedOrEmpty(value)) {
    return ''
  }

  // Preserve trailing decimal point during input
  const hasTrailingDecimal = value.endsWith('.')

  // Check if user is typing a decimal value (has decimal point)
  const decimalIndex = value.indexOf('.')
  const hasDecimal = decimalIndex !== -1
  const digitsAfterDecimal = hasDecimal ? value.length - decimalIndex - 1 : 0

  // If user is in the middle of typing decimal places (0 or 1 digit after decimal),
  // or has trailing zeros in the decimal part (within 2 decimal limit), format only the integer part to preserve the decimal state
  // Without this, typing 0 after a decimal point will format that value without the decimal point, and the user cannot continue typing the decimal part.
  const hasTrailingZeroInDecimal = hasDecimal && value.endsWith('0') && digitsAfterDecimal > 0 && digitsAfterDecimal <= 2
  if (hasDecimal && digitsAfterDecimal <= 2 && (digitsAfterDecimal <= 1 || hasTrailingZeroInDecimal)) {
    const [integerPart, decimalPart] = value.split('.')
    const integerValue = parseInt(integerPart, 10)

    if (isNaN(integerValue)) {
      return value
    }

    const formattedInteger = new Intl.NumberFormat(locale, {
      useGrouping: true,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(integerValue)

    return `${formattedInteger}.${decimalPart}`
  }

  const numericValue = parseFloat(value)
  if (isNaN(numericValue)) {
    return value
  }

  const formatted = new Intl.NumberFormat(locale, {
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numericValue)

  return hasTrailingDecimal ? formatted + '.' : formatted
}

export const getDisplayValue = (rawValue: string, locale?: string): string => {
  if (isNullOrUndefined(locale) || isUndefinedOrEmpty(locale)) {
    return rawValue
  }

  return formatWithLocale(rawValue, locale)
}

// The size is adjusted to account for non-digit characters (commas, decimal points) which typically render narrower than digits in monospace contexts.
export const getInputSize = (value: string, placeholder?: string, isMobileBrowser?: boolean): number => {
  if (isMobileBrowser) {
    // 0 non-digits: +1 (all digits need extra space)
    // 1 non-digit: 0 (one separator needs no adjustment)
    // 2+ non-digits: -1 (multiple separators need reduced space)
    const nonDigitCount = value.replace(/\d/g, '').length
    const sizeAdjustment = nonDigitCount === 0 ? 1 : nonDigitCount >= 2 ? -1 : 0
    return value.length + sizeAdjustment
  }

  if (value.length > 0) {
    // Non-digit characters (commas, decimal points) are typically narrower, so we subtract 1 from the length to better match the visual width.
    // This is not a perfect solution, but it's a good enough approximation.
    const hasNonDigits = /\D/.test(value)
    const sizeAdjustment = hasNonDigits ? 1 : 0
    return value.length - sizeAdjustment
  }

  if (!isNullOrUndefined(placeholder) && placeholder.length > 0) {
    return placeholder.length
  }

  return DEFAULT_INPUT_SIZE
}
